import { useState, useEffect, useCallback } from 'react'

/**
 * Custom hook for managing state in URL query parameters
 * @param {string} key - The query parameter key
 * @param {Object} options - Configuration options
 * @param {string} options.defaultValue - Default value when query param is not present
 * @param {Function} options.serialize - Custom serialization function (optional)
 * @param {Function} options.parse - Custom parsing function (optional)
 * @returns {[string, Function]} - [value, setValue] similar to useState
 */
export const useQueryState = (key, options = {}) => {
  const {
    defaultValue = '',
    serialize = (value) => String(value),
    parse = (value) => value
  } = options

  // Get initial value from URL or use default
  const getInitialValue = useCallback(() => {
    if (typeof window === 'undefined') return defaultValue

    const urlParams = new URLSearchParams(window.location.search)
    const paramValue = urlParams.get(key)

    if (paramValue !== null) {
      try {
        return parse(paramValue)
      } catch (error) {
        console.warn(`Failed to parse query parameter "${key}":`, error)
        return defaultValue
      }
    }

    return defaultValue
  }, [key, defaultValue, parse])

  const [value, setValue] = useState(getInitialValue)

  // Update URL when value changes
  const updateURL = useCallback((newValue) => {
    if (typeof window === 'undefined') return

    const url = new URL(window.location)
    const serializedValue = serialize(newValue)

    if (serializedValue === serialize(defaultValue)) {
      // Remove parameter if it's the default value
      url.searchParams.delete(key)
    } else {
      // Set the parameter
      url.searchParams.set(key, serializedValue)
    }

    // Update URL without triggering a page reload
    const newURL = url.pathname + url.search
    window.history.replaceState({}, '', newURL)
  }, [key, defaultValue, serialize])

  // Listen for browser navigation (back/forward)
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handlePopState = () => {
      const newValue = getInitialValue()
      setValue(newValue)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [getInitialValue])

  // Custom setter that updates both state and URL
  const setQueryState = useCallback((newValue) => {
    const resolvedValue = typeof newValue === 'function' ? newValue(value) : newValue
    setValue(resolvedValue)
    updateURL(resolvedValue)
  }, [value, updateURL])

  return [value, setQueryState]
}
